import { memo, useCallback } from 'react'

import { useUnit } from 'effector-react'

import { Tooltip } from '@/components/ui/tooltip'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import {
  $focusedScriptId,
  $scriptsFilter,
  getItemIdentifier,
  isItemFocused,
} from '@/models/scripts/chatting.ts'
import type { ScriptNode } from '@/modules/scripts/types'
import { classNames } from '@/utils/helpers'
import { getFormattedMessageByMessageObject } from '@/utils/message-formatter'

import { escapeRegExp } from '../../utils'
import { ChatScriptMediaCounter } from '../chat-script-media-counter'

interface Props {
  script: ScriptNode
  onClick: (script: ScriptNode) => void
  isSent?: boolean
}

export const ChatScriptItem = memo(({ script, onClick, isSent }: Props) => {
  const activeChat = useUnit($activeChatDetails)
  const focusedScriptId = useUnit($focusedScriptId)
  const text = useUnit($scriptsFilter)
  const includesText = text
    ? script?.message?.text?.toLowerCase()?.includes(text.toLowerCase())
    : false
  const formattedText = activeChat?.withUser
    ? getFormattedMessageByMessageObject(script.message, activeChat?.withUser)
    : script.message?.text || ''

  const regex = new RegExp(escapeRegExp(text), 'gi')

  const highlightedText = formattedText?.replace(regex, (match) => {
    // Preserve the case of the original match
    return `<span class="bg-primary-200 dark:bg-primary-700">${match}</span>`
  })

  const handleScriptClick = useCallback(() => {
    onClick(script)
  }, [onClick, script])

  return (
    <div
      data-id={getItemIdentifier(script)}
      className={classNames(
        'flex cursor-pointer items-center p-2.5 py-2 hover:bg-primary-50 dark:hover:bg-primary-900',
        {
          'bg-primary-50 dark:bg-primary-900': isItemFocused(
            script,
            focusedScriptId,
          ),
        },
      )}
      tabIndex={-1}
      onClick={handleScriptClick}
      data-testid="script-item"
    >
      <span className="font-semibold text-text-default">
        {isSent ? (
          <Tooltip
            asChild
            content="Message already sent to this fan"
          >
            <span
              className={classNames(
                'ml-auto rounded-lg px-1.5 py-0.5 text-base',
                'bg-yellow-500 text-white',
              )}
            >
              {script.name}
            </span>
          </Tooltip>
        ) : (
          script.name
        )}{' '}
      </span>
      {includesText ? (
        <span
          className="ml-2 font-medium text-secondary-300"
          dangerouslySetInnerHTML={{
            __html: highlightedText ?? '',
          }}
        />
      ) : (
        <span className="ml-2 font-medium text-secondary-300">
          {activeChat?.withUser ? formattedText : script.message?.text}
        </span>
      )}
      <span className="ml-auto mr-2 font-semibold text-text-default">
        {' '}
        {script.message?.medias?.length
          ? `(${script.message.medias.length})`
          : ''}
      </span>
      <ChatScriptMediaCounter script={script} />
    </div>
  )
})

ChatScriptItem.displayName = 'ChatScriptItem'
